import React, { ReactNode } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CustomModal, SpinnerLoader } from "../../index";
import { useTranslation, Trans } from "react-i18next";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";

interface EmployeeLimitModalProps {
    showEmployeeLimitModal: boolean;
    handleCloseEmployeeLimitModal: () => void;
    isCreateEmployee?: boolean;
    isCheckLimitBeforeTutorial?: boolean;
    isSubmitting?: boolean;
    confirming?: boolean;
    handleConfirmClick?: () => void;
}

const EmployeeLimitModal: React.FC<EmployeeLimitModalProps> = ({
    showEmployeeLimitModal,
    handleCloseEmployeeLimitModal,
    isCreateEmployee = false,
    isCheckLimitBeforeTutorial = false,
    isSubmitting,
    confirming,
    handleConfirmClick,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);

    const textBodyP1 = isCreateEmployee
        ? "Your tariff plan includes a limit on the number of employees in the company (6 employees + 1 owner). The current employee will be created with access disabled."
        : "Your tariff plan includes a limit on the number of employees in the company (6 employees + 1 owner).";

    let textBodyP2: string;
    if (isCheckLimitBeforeTutorial) {
        textBodyP2 =
            "To create an employee, you first need to disable access for any other employee. To remove the restriction on the number of employees, connect the tariff";
    } else if (isCreateEmployee) {
        textBodyP2 =
            "You can enable it in the employee list by first disabling any other employee. To remove the limit on the number of employees, connect the tariff";
    } else {
        textBodyP2 =
            "To enable employee access, you first need to disable access for any other employee. To remove the restriction on the number of employees, connect the tariff";
    }

    const buttons = isCreateEmployee
        ? [
              {
                  text: t("cancel"),
                  onClick: handleCloseEmployeeLimitModal,
                  variant: "outline-primary",
                  className: "w-100 fw-normal",
              },
              {
                  text: isSubmitting ? <SpinnerLoader variant="light" loadingText={t("Creating...")} /> : "OK",
                  onClick: handleConfirmClick,
                  variant: "primary",
                  className: "w-100 fw-normal",
                  disabled: confirming || isSubmitting,
              },
          ]
        : [
              {
                  text: t("cancel"),
                  onClick: handleCloseEmployeeLimitModal,
                  variant: "outline-primary",
                  className: "w-100 fw-normal",
              },
              {
                  text: t('Connect "Premium"'),
                  onClick: () => navigate(`/payment/company/${currentCompanyId}/tariff/${branchId}`),
                  variant: "primary",
                  className: "w-100 fw-normal",
              },
          ];

    return (
        <CustomModal
            show={showEmployeeLimitModal}
            handleClose={handleCloseEmployeeLimitModal}
            headerAlignment="center"
            title={
                <Container>
                    <Row>
                        <h5 className="m-0 p-0 fw-normal">{t("Limit on Number of Employees")}</h5>
                    </Row>
                </Container>
            }
            body={
                <Container>
                    <Row>
                        <Col>
                            <p>{t(textBodyP1)}</p>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <p className="mb-0">
                                <Trans
                                    i18nKey={t(textBodyP2)}
                                    components={{
                                        premiumLink: (
                                            <Link to={`/payment/company/${currentCompanyId}/tariff/${branchId}`} />
                                        ),
                                    }}
                                />
                            </p>
                        </Col>
                    </Row>
                </Container>
            }
            buttons={buttons}
        />
    );
};

export default EmployeeLimitModal;
